import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const WhatsAppCard = ({ isHero = false, hideQR = false }) => {
    const ROTATOR_LINK = `${process.env.REACT_APP_API_URL}/join-whatsapp`;

    // Mouse tilt effect
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseXSpring = useSpring(x);
    const mouseYSpring = useSpring(y);

    const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["7deg", "-7deg"]);
    const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-7deg", "7deg"]);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const xPct = mouseX / width - 0.5;
        const yPct = mouseY / height - 0.5;

        x.set(xPct);
        y.set(yPct);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    const products = [
        {
            src: "/assets/banner/medicine.png",
            style: { top: isHero ? '10%' : '-20px', left: '-5%', width: isHero ? '220px' : '180px', rotate: '-10deg', zIndex: 1 },
            delay: 0,
            duration: 4
        },
        {
            src: "/assets/banner/stethoscope.png",
            style: { bottom: isHero ? '5%' : '-30px', left: '20%', width: isHero ? '190px' : '160px', rotate: '15deg', zIndex: 2 },
            delay: 0.5,
            duration: 5
        },
        {
            src: "/assets/banner/cross.png",
            style: { top: isHero ? '15%' : '20px', left: '45%', width: isHero ? '250px' : '200px', rotate: '-5deg', zIndex: 3 },
            delay: 1,
            duration: 6
        },
        {
            src: "/assets/banner/thermometer.png",
            style: { bottom: isHero ? '10%' : '10px', left: '80%', width: isHero ? '170px' : '140px', rotate: '10deg', zIndex: 1 },
            delay: 0.2,
            duration: 4.5
        }
    ];

    return (
        <motion.a
            href={ROTATOR_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
                rotateX,
                rotateY,
                transformStyle: "preserve-3d",
                display: 'block',
                textDecoration: 'none',
                height: isHero ? '100%' : 'auto'
            }}
            initial={isHero ? {} : { opacity: 0, y: 30 }}
            whileInView={isHero ? {} : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
        >
            <div className="whatsapp-banner-horizontal" style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)',
                borderRadius: isHero ? '0' : '20px',
                padding: isHero ? '0 100px' : '40px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: isHero ? '450px' : '220px',
                height: isHero ? '100%' : 'auto',
                overflow: 'hidden',
                border: isHero ? 'none' : '1px solid rgba(255, 255, 255, 0.05)',
                boxShadow: isHero ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                cursor: 'pointer'
            }}>
                {/* Background Glow */}
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '120%',
                    height: '120%',
                    background: 'radial-gradient(circle, rgba(37, 211, 102, 0.08) 0%, transparent 70%)',
                    pointerEvents: 'none',
                    zIndex: 0
                }} />

                {/* Left: QR Code Section */}
                <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <motion.div 
                        whileHover={{ scale: 1.1 }}
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            padding: '12px',
                            borderRadius: '20px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                        }}
                    >
                        <QRCodeCanvas value={ROTATOR_LINK} size={110} level="H" bgColor="transparent" fgColor="#ffffff" />
                        <motion.div 
                            animate={{ top: ['0%', '100%', '0%'] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                            style={{
                                position: 'absolute',
                                left: '12px',
                                right: '12px',
                                height: '2px',
                                background: 'linear-gradient(90deg, transparent, #25D366, transparent)',
                                opacity: 0.5,
                                zIndex: 3
                            }}
                        />
                    </motion.div>
                </div>

                {/* Center: Desired Products */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '30%',
                    right: '35%',
                    zIndex: 1,
                    pointerEvents: 'none'
                }}>
                    {products.map((product, idx) => (
                        <motion.img
                            key={idx}
                            src={product.src}
                            alt="Desejo"
                            style={{
                                position: 'absolute',
                                ...product.style,
                                filter: 'contrast(1.5) brightness(1.2) drop-shadow(0 0 15px rgba(34, 197, 94, 0.4))',
                                pointerEvents: 'none',
                                mixBlendMode: 'screen',
                                WebkitMaskImage: 'radial-gradient(circle, black 40%, transparent 85%)',
                                maskImage: 'radial-gradient(circle, black 40%, transparent 85%)'
                            }}
                            animate={{
                                y: [0, -15, 0],
                                rotate: [product.style.rotate, (parseInt(product.style.rotate) + 5) + 'deg', product.style.rotate]
                            }}
                            transition={{
                                duration: product.duration || 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: product.delay
                            }}
                        />
                    ))}
                </div>

                {/* Right: Content Section */}
                <div style={{ 
                    position: 'relative', 
                    zIndex: 2, 
                    textAlign: 'right', 
                    maxWidth: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '16px'
                }}>
                    <div>
                        <h3 style={{ 
                            color: '#ffffff', 
                            fontSize: isHero ? '42px' : '28px', 
                            fontWeight: '900', 
                            lineHeight: '1',
                            marginBottom: '12px',
                            textShadow: '0 4px 20px rgba(0,0,0,0.6)',
                            letterSpacing: '-0.02em'
                        }}>
                            RECEBA OFERTAS EXCLUSIVAS
                        </h3>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: isHero ? '18px' : '14px', fontWeight: '500' }}>
                            Direto no seu WhatsApp, em primeira mão!
                        </p>
                    </div>

                    <motion.div
                        whileHover={{ scale: 1.05, backgroundColor: '#20bd5b' }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                            backgroundColor: '#25D366',
                            color: '#000000',
                            padding: isHero ? '18px 45px' : '14px 32px',
                            borderRadius: '50px',
                            fontWeight: '800',
                            fontSize: isHero ? '18px' : '16px',
                            boxShadow: '0 15px 30px rgba(37, 211, 102, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <i className="fab fa-whatsapp" style={{ fontSize: '22px' }}></i>
                        QUERO ENTRAR
                    </motion.div>
                </div>

                {/* Inline Responsive Styles */}
                <style dangerouslySetInnerHTML={{ __html: `
                    @media (max-width: 768px) {
                        .whatsapp-banner-horizontal {
                            flex-direction: column !important;
                            text-align: center !important;
                            padding: 30px !important;
                            gap: 40px !important;
                        }
                        .whatsapp-banner-horizontal > div:last-child {
                            text-align: center !important;
                            align-items: center !important;
                            max-width: 100% !important;
                        }
                        .whatsapp-banner-horizontal > div:nth-child(3) {
                            display: none !important;
                        }
                    }
                `}} />
            </div>
        </motion.a>
    );
};

export default WhatsAppCard;
